import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Popup from 'reactjs-popup';
import { Button, Conductor, Microphone01, PhoneX, XClose } from '@opendate-app/setlist';
import client from '../../../shared/api/client';
import VenueConductorOrbControls from './VenueConductorOrbControls';
import useOrbPanelPosition from '../hooks/useOrbPanelPosition';
import {
  clampOrbPosition,
  CONDUCTOR_WIDGET_HEIGHT,
  CONDUCTOR_WIDGET_WIDTH,
  getOrbPlacement,
  getOrbSnapCandidate,
  getOrbSnapPoints,
  loadOrbState,
  restoreOrbPosition,
  saveOrbState
} from '../dockState';

const MOVE_STEP = 16;
const LARGE_MOVE_STEP = 48;
const DRAG_THRESHOLD = 5;
const TOUCH_HOLD_DELAY = 500;
const SNAP_SPRING_STIFFNESS = 420;
const SNAP_SPRING_DAMPING = 41;
const SNAP_SETTLE_DISTANCE = 0.5;
const SNAP_SETTLE_SPEED = 3;

const voiceCallPresentation = (voiceCall) => {
  if (voiceCall.status === 'connecting') {
    return { title: 'Connecting…', subtitle: 'One moment', animation: 'deep-sparkle-breath' };
  }
  if (voiceCall.speakingLine) {
    return { title: 'Conductor is speaking', subtitle: `“${voiceCall.speakingLine}”`, animation: 'vocal-shimmer', speaking: true };
  }
  if (voiceCall.activeToolName) {
    return { title: `${voiceCall.activeToolName}…`, subtitle: 'Working on it', animation: 'constellation-orbit' };
  }
  if (voiceCall.activity === 'processing') {
    return { title: 'Thinking…', subtitle: 'One moment', animation: 'star-conversation' };
  }
  if (voiceCall.status === 'paused') {
    return { title: 'Paused', subtitle: 'Mic muted', animation: 'deep-sparkle-breath' };
  }
  return { title: 'Listening…', subtitle: 'Go ahead', animation: 'constellation-twinkle' };
};

const utilityBarHeight = () => {
  const utilityBar = document.getElementById('utility-bar');
  if (utilityBar?.offsetHeight) return utilityBar.offsetHeight;
  const configuredHeight = Number.parseFloat(
    window.getComputedStyle(document.body).getPropertyValue('--utility-bar-height')
  );
  return Number.isFinite(configuredHeight) ? configuredHeight : 0;
};

const reservedBottomHeight = () => Math.max(
  utilityBarHeight(),
  ...Array.from(document.querySelectorAll('[data-conductor-orb-inset]'), (element) => element.offsetHeight)
);

const initialPosition = (teamId, currentUserId) => {
  const stored = loadOrbState(teamId, currentUserId);
  return restoreOrbPosition(
    stored.position,
    stored.placement,
    window.innerWidth,
    window.innerHeight,
    reservedBottomHeight()
  );
};

const VenueConductorOrb = ({
  teamId,
  currentUserId,
  onOpen,
  onStartVoice = null,
  voiceCall = null,
  onEndVoice = null,
  quickChatOpen = false,
  onPositionChange = null,
  onDismiss = null
}) => {
  const [position, setPosition] = useState(() => initialPosition(teamId, currentUserId));
  const [pendingCount, setPendingCount] = useState(0);
  const [previewInsight, setPreviewInsight] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [snapCandidateId, setSnapCandidateId] = useState(null);
  const [positionMenuOpen, setPositionMenuOpen] = useState(false);
  const [keyboardMoveControl, setKeyboardMoveControl] = useState(false);
  const moveControlsEnabled = !quickChatOpen && !previewOpen && !isDragging && !voiceCall;
  const orbRef = useRef(null);
  const positionRef = useRef(position);
  const preferredPositionRef = useRef(position);
  const placementRef = useRef(
    loadOrbState(teamId, currentUserId).placement
      || getOrbPlacement(position, window.innerWidth, window.innerHeight, reservedBottomHeight())
  );
  const dragRef = useRef(null);
  const popupRef = useRef(null);
  const quickChatOpenRef = useRef(quickChatOpen);
  const ignoreNextClickRef = useRef(false);
  const ignoreClickTimerRef = useRef(null);
  const touchHoldTimerRef = useRef(null);
  const resizePersistTimerRef = useRef(null);
  const snapAnimationFrameRef = useRef(null);
  const insightsRequestSequenceRef = useRef(0);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useLayoutEffect(() => {
    quickChatOpenRef.current = quickChatOpen;
    if (quickChatOpen) setPreviewOpen(false);
  }, [quickChatOpen]);

  useEffect(() => {
    if (moveControlsEnabled) return;
    setPositionMenuOpen(false);
    window.clearTimeout(touchHoldTimerRef.current);
  }, [moveControlsEnabled]);

  useEffect(() => {
    if (previewOpen) popupRef.current?.setPosition();
  }, [position, previewOpen]);

  const persistPosition = useCallback((
    nextPosition,
    snapPointId = null,
    bottomInset = reservedBottomHeight()
  ) => {
    const placement = getOrbPlacement(
      nextPosition,
      window.innerWidth,
      window.innerHeight,
      bottomInset,
      snapPointId
    );
    placementRef.current = placement;
    preferredPositionRef.current = nextPosition;
    saveOrbState(teamId, currentUserId, { position: nextPosition, placement });
  }, [teamId, currentUserId]);

  const setClampedPosition = useCallback((nextPosition, persist = false, bottomInset = reservedBottomHeight()) => {
    const clamped = clampOrbPosition(
      nextPosition,
      window.innerWidth,
      window.innerHeight,
      bottomInset
    );
    const unchanged = clamped.x === positionRef.current.x && clamped.y === positionRef.current.y;
    positionRef.current = clamped;
    if (!unchanged) setPosition(clamped);
    if (persist && !unchanged) persistPosition(clamped, null, bottomInset);
    return clamped;
  }, [persistPosition]);

  const panelPosition = useOrbPanelPosition({
    getPreferredPosition: () => restoreOrbPosition(preferredPositionRef.current, placementRef.current,
      window.innerWidth, window.innerHeight, reservedBottomHeight()),
    getPosition: () => positionRef.current,
    isDragging: () => Boolean(dragRef.current || snapAnimationFrameRef.current !== null),
    moveTo: (target) => {
      const nextPosition = setClampedPosition(target);
      onPositionChange?.(nextPosition);
    }
  });

  const stopPanelMovement = () => {
    if (panelPosition.panelMovement) {
      const bounds = orbRef.current?.parentElement?.getBoundingClientRect();
      if (bounds?.width) {
        const pausedPosition = setClampedPosition({ x: bounds.left, y: bounds.top });
        onPositionChange?.(pausedPosition);
      }
    }
    panelPosition.stopPanelMovement();
  };

  const cancelSnapAnimation = useCallback(() => {
    if (snapAnimationFrameRef.current === null) return false;
    window.cancelAnimationFrame(snapAnimationFrameRef.current);
    snapAnimationFrameRef.current = null;
    return true;
  }, []);

  const animatePositionTo = useCallback((targetPosition, snapPointId, bottomInset) => {
    cancelSnapAnimation();
    placementRef.current = getOrbPlacement(
      targetPosition,
      window.innerWidth,
      window.innerHeight,
      bottomInset,
      snapPointId
    );

    const finish = () => {
      snapAnimationFrameRef.current = null;
      const settledPosition = setClampedPosition(targetPosition, false, bottomInset);
      persistPosition(settledPosition, snapPointId, bottomInset);
      onPositionChange?.(settledPosition);
    };

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      finish();
      return;
    }

    const motion = {
      x: positionRef.current.x,
      y: positionRef.current.y,
      velocityX: 0,
      velocityY: 0,
      lastTime: null
    };

    const step = (time) => {
      if (motion.lastTime === null) {
        motion.lastTime = time;
        snapAnimationFrameRef.current = window.requestAnimationFrame(step);
        return;
      }

      const deltaTime = Math.min((time - motion.lastTime) / 1000, 0.032);
      motion.lastTime = time;
      const accelerationX = SNAP_SPRING_STIFFNESS * (targetPosition.x - motion.x)
        - SNAP_SPRING_DAMPING * motion.velocityX;
      const accelerationY = SNAP_SPRING_STIFFNESS * (targetPosition.y - motion.y)
        - SNAP_SPRING_DAMPING * motion.velocityY;
      motion.velocityX += accelerationX * deltaTime;
      motion.velocityY += accelerationY * deltaTime;
      motion.x += motion.velocityX * deltaTime;
      motion.y += motion.velocityY * deltaTime;
      setClampedPosition({ x: motion.x, y: motion.y }, false, bottomInset);

      const remainingDistance = Math.hypot(targetPosition.x - motion.x, targetPosition.y - motion.y);
      const speed = Math.hypot(motion.velocityX, motion.velocityY);
      if (remainingDistance <= SNAP_SETTLE_DISTANCE && speed <= SNAP_SETTLE_SPEED) {
        finish();
        return;
      }
      snapAnimationFrameRef.current = window.requestAnimationFrame(step);
    };

    snapAnimationFrameRef.current = window.requestAnimationFrame(step);
  }, [cancelSnapAnimation, onPositionChange, persistPosition, setClampedPosition]);

  const finishDrag = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag || (event && event.pointerId !== drag.pointerId)) return;
    window.clearTimeout(touchHoldTimerRef.current);
    window.removeEventListener('pointermove', drag.onMove);
    window.removeEventListener('pointerup', drag.onFinish);
    window.removeEventListener('pointercancel', drag.onFinish);
    const moved = drag.moved;
    const snapPointId = drag.snapCandidateId;
    const bottomInset = drag.bottomInset;
    dragRef.current = null;
    setIsDragging(false);
    setSnapCandidateId(null);
    document.body.classList.remove('conductor-orb-dragging');
    if (moved || drag.held) {
      ignoreNextClickRef.current = true;
      window.clearTimeout(ignoreClickTimerRef.current);
      ignoreClickTimerRef.current = window.setTimeout(() => {
        ignoreNextClickRef.current = false;
      }, drag.held ? 400 : 0);
    }
    if (moved) {
      const snapPoint = snapPointId
        ? getOrbSnapPoints(window.innerWidth, window.innerHeight, bottomInset)
          .find((point) => point.id === snapPointId)
        : null;
      if (snapPoint) {
        animatePositionTo(snapPoint, snapPoint.id, bottomInset);
      } else {
        persistPosition(positionRef.current, null, bottomInset);
        onPositionChange?.(positionRef.current);
      }
    }
  }, [animatePositionTo, onPositionChange, persistPosition]);

  const handlePointerMove = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;
    if (drag.held) return;
    if (event.pointerType === 'mouse' && event.buttons === 0) {
      finishDrag(event);
      return;
    }
    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD) return;

    if (!drag.moved) {
      window.clearTimeout(touchHoldTimerRef.current);
      drag.moved = true;
      setIsDragging(true);
      setPreviewOpen(false);
      document.body.classList.add('conductor-orb-dragging');
    }
    const nextPosition = setClampedPosition(
      { x: drag.originX + deltaX, y: drag.originY + deltaY },
      false,
      drag.bottomInset
    );
    const snapCandidate = getOrbSnapCandidate(
      nextPosition,
      window.innerWidth,
      window.innerHeight,
      drag.bottomInset
    );
    const nextSnapCandidateId = snapCandidate?.id || null;
    if (drag.snapCandidateId !== nextSnapCandidateId) {
      drag.snapCandidateId = nextSnapCandidateId;
      setSnapCandidateId(nextSnapCandidateId);
    }
  }, [finishDrag, setClampedPosition]);

  useEffect(() => {
    return () => {
      const drag = dragRef.current;
      if (drag) {
        window.removeEventListener('pointermove', drag.onMove);
        window.removeEventListener('pointerup', drag.onFinish);
        window.removeEventListener('pointercancel', drag.onFinish);
      }
      window.clearTimeout(ignoreClickTimerRef.current);
      window.clearTimeout(touchHoldTimerRef.current);
      window.clearTimeout(resizePersistTimerRef.current);
      cancelSnapAnimation();
      document.body.classList.remove('conductor-orb-dragging');
    };
  }, [cancelSnapAnimation]);

  useEffect(() => {
    const handleResize = () => {
      const previous = positionRef.current;
      cancelSnapAnimation();
      panelPosition.stopPanelMovement();
      const bottomInset = reservedBottomHeight();
      const restored = restoreOrbPosition(
        preferredPositionRef.current,
        placementRef.current,
        window.innerWidth,
        window.innerHeight,
        bottomInset
      );
      preferredPositionRef.current = restored;
      setClampedPosition(panelPosition.displaced ? positionRef.current : restored, false, bottomInset);
      panelPosition.syncPosition(true);
      if (previous.x !== positionRef.current.x || previous.y !== positionRef.current.y) {
        onPositionChange?.(positionRef.current);
        window.clearTimeout(resizePersistTimerRef.current);
        resizePersistTimerRef.current = window.setTimeout(() => {
          saveOrbState(teamId, currentUserId, {
            position: preferredPositionRef.current,
            placement: placementRef.current
          });
        }, 150);
      }
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('conductorOrbInsetChanged', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('conductorOrbInsetChanged', handleResize);
      window.clearTimeout(resizePersistTimerRef.current);
    };
  }, [panelPosition.displaced, panelPosition.syncPosition, panelPosition.stopPanelMovement, cancelSnapAnimation, currentUserId, onPositionChange, setClampedPosition, teamId]);

  const fetchInsights = useCallback(() => {
    const requestSequence = insightsRequestSequenceRef.current + 1;
    insightsRequestSequenceRef.current = requestSequence;
    return client.get('/conductor/venue_chat/insights', { headers: { Accept: 'application/json' } })
      .then(({ data }) => {
        if (requestSequence !== insightsRequestSequenceRef.current) return;
        const insights = data?.insights || [];
        const pending = insights.filter((insight) => insight.status === 'pending');
        const newest = pending.reduce((latest, insight) => {
          if (!latest) return insight;
          return Date.parse(insight.created_at) > Date.parse(latest.created_at) ? insight : latest;
        }, null);
        const stored = loadOrbState(teamId, currentUserId);
        setPendingCount(pending.length);
        setPreviewInsight(newest);
        window.dispatchEvent(new CustomEvent('conductorInsightsChanged', {
          detail: { pendingCount: pending.length }
        }));
        if (newest && stored.lastPreviewedInsightId !== newest.id && !quickChatOpenRef.current) {
          setPreviewOpen(true);
          saveOrbState(teamId, currentUserId, { lastPreviewedInsightId: newest.id });
        }
      })
      .catch(() => {});
  }, [teamId, currentUserId]);

  useEffect(() => {
    fetchInsights();
    const handleRefreshRequested = () => fetchInsights();
    window.addEventListener('conductorInsightsRefreshRequested', handleRefreshRequested);
    return () => {
      window.removeEventListener('conductorInsightsRefreshRequested', handleRefreshRequested);
      insightsRequestSequenceRef.current += 1;
    };
  }, [fetchInsights]);

  useEffect(() => {
    const handleInsightsChanged = (event) => {
      const nextCount = Math.max(0, Number(event?.detail?.pendingCount) || 0);
      setPendingCount(nextCount);
      if (nextCount === 0) {
        setPreviewOpen(false);
        setPreviewInsight(null);
      }
    };
    window.addEventListener('conductorInsightsChanged', handleInsightsChanged);
    return () => window.removeEventListener('conductorInsightsChanged', handleInsightsChanged);
  }, []);

  const handlePointerDown = (event) => {
    if (event.button !== 0 || dragRef.current) return;
    ignoreNextClickRef.current = false;
    window.clearTimeout(ignoreClickTimerRef.current);
    stopPanelMovement();
    const bottomInset = reservedBottomHeight();
    if (cancelSnapAnimation()) persistPosition(positionRef.current, null, bottomInset);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: positionRef.current.x,
      originY: positionRef.current.y,
      moved: false,
      snapCandidateId: null,
      bottomInset,
      onMove: handlePointerMove,
      onFinish: finishDrag
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', finishDrag);
    window.addEventListener('pointercancel', finishDrag);
    if (event.pointerType === 'touch' && moveControlsEnabled) {
      touchHoldTimerRef.current = window.setTimeout(() => {
        if (!dragRef.current || dragRef.current.moved) return;
        dragRef.current.held = true;
        setPositionMenuOpen(true);
      }, TOUCH_HOLD_DELAY);
    }
  };

  const handleContextMenu = (event) => {
    event.preventDefault();
    if (!moveControlsEnabled) return;
    window.clearTimeout(touchHoldTimerRef.current);
    if (dragRef.current) dragRef.current.held = true;
    setPositionMenuOpen(true);
  };

  const handleClick = () => {
    if (ignoreNextClickRef.current) {
      window.clearTimeout(ignoreClickTimerRef.current);
      ignoreNextClickRef.current = false;
      return;
    }
    setPreviewOpen(false);
    onOpen(null, positionRef.current);
  };

  const handleKeyDown = (event) => {
    const movement = event.shiftKey ? LARGE_MOVE_STEP : MOVE_STEP;
    const offsets = {
      ArrowLeft: { x: -movement, y: 0 },
      ArrowRight: { x: movement, y: 0 },
      ArrowUp: { x: 0, y: -movement },
      ArrowDown: { x: 0, y: movement }
    };
    const offset = offsets[event.key];
    if (!offset) return;
    event.preventDefault();
    panelPosition.stopPanelMovement();
    cancelSnapAnimation();
    const nextPosition = setClampedPosition({
      x: positionRef.current.x + offset.x,
      y: positionRef.current.y + offset.y
    }, true);
    onPositionChange?.(nextPosition);
  };

  const moveToSnapPoint = (snapPointId) => {
    const bottomInset = reservedBottomHeight();
    const point = getOrbSnapPoints(window.innerWidth, window.innerHeight, bottomInset)
      .find((candidate) => candidate.id === snapPointId);
    panelPosition.stopPanelMovement();
    cancelSnapAnimation();
    const nextPosition = setClampedPosition(point);
    persistPosition(nextPosition, snapPointId, bottomInset);
    onPositionChange?.(nextPosition);
    orbRef.current?.focus();
  };

  const handleInsightOpen = () => {
    const prompt = previewInsight?.prompt || null;
    setPreviewOpen(false);
    onOpen(prompt, positionRef.current);
  };

  const countLabel = pendingCount > 99 ? '99+' : pendingCount;
  const call = voiceCall ? voiceCallPresentation(voiceCall) : null;
  const wrapperStyle = {
    '--conductor-orb-x': `${position.x}px`,
    '--conductor-orb-y': `${position.y}px`
  };
  const snapPoints = isDragging
    ? getOrbSnapPoints(window.innerWidth, window.innerHeight, reservedBottomHeight())
    : [];

  return (
    <>
      {isDragging && (
        <div className="venue-conductor-orb-snap-targets" aria-hidden="true">
          {snapPoints.map((snapPoint) => (
            <span
              key={snapPoint.id}
              className={`venue-conductor-orb-snap-target${snapCandidateId === snapPoint.id ? ' is-active' : ''}`}
              data-testid="venue-conductor-orb-snap-target"
              data-snap-point={snapPoint.id}
              style={{
                '--conductor-orb-snap-x': `${snapPoint.x + (CONDUCTOR_WIDGET_WIDTH / 2)}px`,
                '--conductor-orb-snap-y': `${snapPoint.y + (CONDUCTOR_WIDGET_HEIGHT / 2)}px`
              }}
            />
          ))}
        </div>
      )}
      <Popup
      ref={popupRef}
      trigger={(
        <div
          className={`venue-conductor-orb-anchor${isDragging ? ' is-dragging' : ''}${keyboardMoveControl ? ' show-keyboard-move-control' : ''}${panelPosition.panelMovement ? ' is-moving-with-panel' : ''}`}
          style={wrapperStyle}
          onKeyDownCapture={() => setKeyboardMoveControl(true)}
          onPointerDownCapture={() => setKeyboardMoveControl(false)}
          onPointerLeave={() => setKeyboardMoveControl(false)}
        >
          {call ? (
            <div
              className={`venue-conductor-orb is-voice-call${call.speaking ? ' is-speaking' : ''}`}
              data-align-x={position.x + (CONDUCTOR_WIDGET_WIDTH / 2) > window.innerWidth / 2 ? 'right' : 'left'}
              data-align-y={position.y + (CONDUCTOR_WIDGET_HEIGHT / 2) > window.innerHeight / 2 ? 'bottom' : 'top'}
              data-testid="venue-conductor-voice-call"
            >
              <Conductor className="venue-conductor-orb-sparkle" width={22} height={22} animation={call.animation} />
              <div className="venue-conductor-orb-call-copy" role="status" aria-live="polite">
                <strong>{call.title}</strong>
                <span>{call.subtitle}</span>
              </div>
              <button
                type="button"
                className="venue-conductor-orb-end-call"
                aria-label="End voice call"
                onClick={onEndVoice}
              >
                <PhoneX width={16} height={16} aria-hidden="true" />
              </button>
            </div>
          ) : (
            <div className={`venue-conductor-orb${pendingCount > 0 ? ' has-insights' : ''}${quickChatOpen ? ' is-expanded' : ''}`}>
              <button
                type="button"
                className="venue-conductor-orb-ask"
                ref={orbRef}
                data-testid="venue-conductor-orb"
                aria-label={`Open Conductor${pendingCount > 0 ? `, ${pendingCount} new insight${pendingCount === 1 ? '' : 's'}` : ''}`}
                aria-expanded={quickChatOpen}
                aria-describedby="venue-conductor-orb-instructions"
                onPointerDown={handlePointerDown}
                onContextMenu={handleContextMenu}
                onTouchEnd={(event) => {
                  if (dragRef.current?.held || ignoreNextClickRef.current) event.preventDefault();
                }}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
              >
                <Conductor className="venue-conductor-orb-sparkle" width={20} height={20} />
                <span className="venue-conductor-orb-label">
                  <span className="venue-conductor-orb-label-idle">Ask Conductor…</span>
                  <span className="venue-conductor-orb-label-hover" aria-hidden="true">Drag to move</span>
                </span>
                {pendingCount > 0 && (
                  <span className="venue-conductor-orb-count" data-testid="conductor-orb-count">
                    {countLabel}
                  </span>
                )}
              </button>
              {onStartVoice && (
                <button
                  type="button"
                  className="venue-conductor-orb-mic"
                  aria-label="Talk to Conductor"
                  onClick={onStartVoice}
                >
                  <Microphone01 width={16} height={16} aria-hidden="true" />
                </button>
              )}
              {onDismiss && (
                <Button
                  variant="tertiary"
                  size="sm"
                  icon={XClose}
                  className="venue-conductor-orb-dismiss"
                  aria-label="Hide Conductor widget"
                  data-testid="venue-conductor-orb-dismiss"
                  onClick={onDismiss}
                />
              )}
            </div>
          )}
          <VenueConductorOrbControls
            enabled={moveControlsEnabled}
            menuOpen={positionMenuOpen}
            onMove={moveToSnapPoint} onOpenChange={setPositionMenuOpen}
            onPointerDown={handlePointerDown}
            onClickCapture={(event) => {
              if (!ignoreNextClickRef.current) return;
              event.preventDefault();
              event.stopPropagation();
              ignoreNextClickRef.current = false;
            }}
          />
          <span id="venue-conductor-orb-instructions" className="sr-only">
            Drag to move. Release near a highlighted edge point to snap. When focused, use the arrow keys to reposition. Touch and hold or right-click for position choices.
          </span>
        </div>
      )}
      open={previewOpen && Boolean(previewInsight)}
      on={[]}
      onClose={() => setPreviewOpen(false)}
      position={['top right', 'top left', 'bottom right', 'bottom left', 'left center', 'right center']}
      offsetY={12}
      keepTooltipInside
      arrow={false}
      closeOnDocumentClick={false}
      className="venue-conductor-orb-popup"
      contentStyle={{
        width: 'min(320px, calc(100vw - 32px))',
        padding: 0,
        border: 'none',
        background: 'transparent',
        boxShadow: 'none',
        zIndex: 1050
      }}
    >
      <section className="venue-conductor-orb-insight-card" data-testid="conductor-orb-insight-card">
        <div className="venue-conductor-orb-insight-heading">
          <Conductor width={20} height={20} />
          <span className="setlist-text-xs setlist-font-semibold">Conductor insight</span>
          <Button
            variant="tertiary"
            size="sm"
            icon={XClose}
            className="venue-conductor-orb-insight-close"
            aria-label="Close insight preview"
            onClick={() => setPreviewOpen(false)}
          />
        </div>
        <h2 className="venue-conductor-orb-insight-title setlist-text-sm setlist-font-semibold">
          {previewInsight?.title}
        </h2>
        {previewInsight?.body && (
          <p className="venue-conductor-orb-insight-body setlist-text-xs">
            {previewInsight.body}
          </p>
        )}
        <div className="venue-conductor-orb-insight-actions">
          <Button variant="primary" size="sm" onClick={handleInsightOpen}>
            Open insight
          </Button>
          <Button variant="tertiary" size="sm" onClick={() => setPreviewOpen(false)}>
            Not now
          </Button>
        </div>
      </section>
      </Popup>
    </>
  );
};

VenueConductorOrb.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  currentUserId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onOpen: PropTypes.func.isRequired,
  onStartVoice: PropTypes.func,
  voiceCall: PropTypes.shape({
    status: PropTypes.string.isRequired,
    activity: PropTypes.string,
    activeToolName: PropTypes.string,
    speakingLine: PropTypes.string
  }),
  onEndVoice: PropTypes.func,
  quickChatOpen: PropTypes.bool,
  onPositionChange: PropTypes.func,
  onDismiss: PropTypes.func
};

export default VenueConductorOrb;
